import type { ChannelSummaryType } from 'ChannelSummary/types'
import classNames from 'classnames'
import { ClubCardModalContainer } from 'ClubCardModal'
import InfoElement from 'components/InfoElement'
import type { DetailsData } from 'data/types'
import React, { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { label, orange } from 'styles'
import { getDifferenceWithPrecision, useToggle } from 'utils'

import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'

import useStyles from './useStyles'

export interface ClubCardsDetailsProps {
  data: DetailsData
  type: ChannelSummaryType
}

function ClubCardsDetails({ data, type }: ClubCardsDetailsProps) {
  const { t: translate } = useTranslation()
  const { isToggled, toggle } = useToggle()
  const classes = useStyles()
  const showClubCard = (data.clubShareCurrent || 0) > 0 || (data.clubSharePercent || 0) > 0 || (data.clubCardsCurrent || 0) > 0

  if (!showClubCard) return null

  return (
    <>
      <Divider />
      <Box padding="1.4rem 1.6rem 1.2rem 0.8rem" display="flex">
        <Grid container>
          <Grid display="flex" flexDirection="column" justifyContent="center" item xs={2} textAlign="center">
            <div className={classNames('fm fm-clubcard', classes.icon)} />
            <Box color={label}>{translate('revenue.details.clubCard')}</Box>
          </Grid>
          <Grid item xs={5} display="flex" alignItems="baseline">
            <Box ml={1}>% {translate('revenue.stats.sale')}:</Box>
            <Box className={classes.value} ml={1}>
              {
                getDifferenceWithPrecision({
                  number: data.clubShareCurrent,
                  precision: 0,
                  translate,
                }).value
              }
              {type === 'group' && (
                <>
                  <Box ml={0.5} display="inline-block" fontSize="0">
                    <IconButton color="primary" onClick={toggle} className={classes.moreIcon}>
                      <Box className="fm fm-more" color={orange} />
                    </IconButton>
                  </Box>
                  <ClubCardModalContainer isOpen={isToggled} onClose={toggle} />
                </>
              )}
              <InfoElement
                className={classes.percentInfo}
                percentDifference={getDifferenceWithPrecision({
                  handleNullCase: true,
                  number: data.clubSharePercent,
                  precision: 0,
                  translate,
                })}
              />
            </Box>
          </Grid>
          <Grid item xs={4} display="flex" alignItems="baseline">
            <Grid container alignItems="baseline">
              <Grid item xs={4}>
                {translate('revenue.stats.new')}:
              </Grid>

              <Grid item xs={8}>
                <Box className={classes.value} ml={1} display="flex">
                  {data.clubCardsCurrent}
                  {data['clubCards@Current'] !== null && (
                    <Box className={classes.valueSecondary} display="flex" alignItems="center">
                      <Box display="inline" className={classNames('fm fm-phone', classes.phoneIcon)} ml={2} />
                      {data['clubCards@Current']}
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  )
}

export default memo<ClubCardsDetailsProps>(ClubCardsDetails)
